import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface InsuranceCertificateDialogProps {
  data: any
  shipmentId: string
  documentId: string
  onSave: (data: any, changes?: any[]) => void
}

const InsuranceCertificateDialog: React.FC<InsuranceCertificateDialogProps> = ({
  data,
  shipmentId,
  documentId,
  onSave,
}) => {
  // Define sections for Insurance Certificate
  const sections: DocumentSection[] = [
    {
      id: 'certificateInfo',
      title: 'Certificate Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Certificate #', path: 'certificateNumber' },
        { type: 'field', label: 'Policy #', path: 'policyNumber' },
        { type: 'field', label: 'Reference #', path: 'referenceNumber' },
        { type: 'field', label: 'Issue Date', path: 'issueDate' },
        { type: 'field', label: 'Effective Date', path: 'effectiveDate' },
        { type: 'field', label: 'Expiry Date', path: 'expiryDate' },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Insurer',
          path: 'insurer',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
          ],
        },
        {
          type: 'object',
          label: 'Insured',
          path: 'insured',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
          ],
        },
        {
          type: 'object',
          label: 'Beneficiary',
          path: 'beneficiary',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'coverage',
      title: 'Coverage Details',
      layout: 'double',
      fields: [
        {
          type: 'object',
          label: 'Coverage',
          path: 'coverage',
          fields: [
            { label: 'Type', path: 'type' },
            { label: 'Amount', path: 'amount' },
            { label: 'Deductible', path: 'deductible' },
          ],
        },
      ],
    },
    {
      id: 'shipmentDetails',
      title: 'Shipment Details',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Shipment Details',
          path: 'shipmentDetails',
          fields: [
            { label: 'Conveyance Type', path: 'conveyanceType' },
            { label: 'Port of Loading', path: 'portOfLoading' },
            { label: 'Port of Discharge', path: 'portOfDischarge' },
            { label: 'Place of Origin', path: 'placeOfOrigin' },
            { label: 'Final Destination', path: 'finalDestination' },
          ],
        },
      ],
    },
    {
      id: 'vessel',
      title: 'Vessel Information',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Vessel',
          path: 'shipmentDetails.vessel',
          fields: [
            { label: 'Vessel Name', path: 'name' },
            { label: 'Voyage', path: 'voyage' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'cargo',
      title: 'Cargo Details',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Cargo',
          path: 'cargo',
          columns: [
            { label: 'Description', path: 'description' },
            { label: 'Packages', path: 'packages' },
            {
              label: 'Value',
              path: 'value',
              complexType: 'currency',
            },
            {
              label: 'Weight',
              path: 'weight',
              complexType: 'weight',
            },
            {
              label: 'Volume',
              path: 'volume',
              complexType: 'volume',
            },
            {
              label: 'Dimensions',
              path: 'dimensions',
              complexType: 'dimensions',
            },
            { label: 'Marks and Numbers', path: 'marksAndNumbers' },
          ],
        },
      ],
    },
    {
      id: 'additionalInfo',
      title: 'Additional Information',
      layout: 'single',
      fields: [
        { type: 'field', label: 'Special Conditions', path: 'specialConditions', fullWidth: true },
        { type: 'field', label: 'Claims Procedure', path: 'claimsProcedure', fullWidth: true },
      ],
    },
    {
      id: 'signatures',
      title: 'Signatures',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Insurer Signature', path: 'insurerSignature' },
        { type: 'field', label: 'Agent Signature', path: 'agentSignature' },
        { type: 'field', label: 'Date of Signature', path: 'dateOfSignature' },
        { type: 'field', label: 'Is Original', path: 'isOriginal' },
      ],
    },
  ]

  return (
    <DocumentDialog
      documentData={data}
      schema={{}}
      sections={sections}
      documentType="Insurance Certificate"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default InsuranceCertificateDialog
